<template>
  <div v-if="options" class="button-row-container">
    <div class="button-row">
      <template v-for="(option, index) in shortOptions" :key="index">
        <button
          class="button-option"
          :class="{
            active: localSelectedOptions.includes(props.options[index]),
            'left-button': index === 0,
            'right-button': index === options.length - 1
          }"
          @click="toggleOption(option)">
          {{ option }}
        </button>
        <div v-if="index < shortOptions.length - 1" :key="`separator-${index}`" class="separator" />
      </template>
    </div>
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`ButtonRow: ${msg}`); // eslint-disable-line
import {ref, computed, onMounted, watch} from "vue";
import {useStorage} from "@vueuse/core";
import {useShort} from "@/composables/useShort";

const props = defineProps(["options", "selectedOptions", "abbreviations"]);
const emit = defineEmits(["update:selectedOptions"]);

const persistedSelectedOptions = useStorage("button-row-selected-options", []);

const filterValidOptions = (options) => options.filter((option) => props.options.includes(option));

if (!Array.isArray(persistedSelectedOptions.value)) persistedSelectedOptions.value = [];
else persistedSelectedOptions.value = filterValidOptions(persistedSelectedOptions.value);

const localSelectedOptions = ref([...persistedSelectedOptions.value]);

const {short} = useShort(props.abbreviations);

const shortOptions = computed(() => props.options.map((option) => short(option)));

function syncFromProps(nextSelectedOptions) {
  if (!Array.isArray(nextSelectedOptions)) return;
  const filtered = filterValidOptions(nextSelectedOptions);
  localSelectedOptions.value = [...filtered];
  persistedSelectedOptions.value = [...filtered];
  log(`syncFromProps ${JSON.stringify(filtered)}`);
}

const toggleOption = (shortenedOption) => {
  const index = shortOptions.value.indexOf(shortenedOption);
  const originalOption = props.options[index];
  if (localSelectedOptions.value.includes(originalOption)) {
    localSelectedOptions.value = localSelectedOptions.value.filter((o) => o !== originalOption);
  } else {
    localSelectedOptions.value.push(originalOption);
  }
  persistedSelectedOptions.value = filterValidOptions([...localSelectedOptions.value]);
  emit("update:selectedOptions", [...localSelectedOptions.value]);
};

watch(
  () => props.selectedOptions,
  (next) => syncFromProps(next),
  {deep: true, immediate: true}
);

onMounted(() => {
  log(`mounted props.selectedOptions=${JSON.stringify(props.selectedOptions)}`);
  if (Array.isArray(props.selectedOptions) && props.selectedOptions.length) return;
  localSelectedOptions.value = filterValidOptions(persistedSelectedOptions.value);
  persistedSelectedOptions.value = [...localSelectedOptions.value];
  emit("update:selectedOptions", [...localSelectedOptions.value]);
});
</script>

<style scoped>
.button-row-container {
  --border-radius: 32px; /* Border radius for the left and right buttons */
  --padding-vertical: 9px; /* Vertical padding for buttons */
  --padding-horizontal: 16px; /* Horizontal padding for buttons */
  --separator-color: #f8f8f8; /* Color of the vertical separators */
  --container-padding: 1px; /* Padding for the outer container */
}

:is(*, *::before, *::after) {
  box-sizing: border-box;
}

.button-row-container {
  display: flex;
  justify-content: center;
  width: 100%;
  background-color: #bfbdbd;
  border-radius: var(--border-radius);
  padding: var(--container-padding);
}
.button-row {
  display: flex;
  gap: 0;
  width: 100%;
}

.button-option {
  flex: 1;
  padding: var(--padding-vertical) var(--padding-horizontal);
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  border: 1px solid transparent;
  border-left: none;
  text-align: center;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.button-option.left-button {
  border-radius: var(--border-radius) 0 0 var(--border-radius);
}

.button-option.right-button {
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
}
.button-option:not(.left-button):not(.right-button) {
  border-radius: 0;
}
.button-option.active {
  color: white;
  background-color: #0a8;
}
.button-option:not(.active) {
  background-color: #f0f0f0;
  color: #727272;
  font-weight: 600;
}

.separator {
  left: 0;
  top: 0;
  bottom: 0; /* full height of the 2nd button (its parent box) */
  width: 1.4px;
  background: rgb(187, 187, 187); /* or var(--separator-color) */
  transform: translateX(-0.5px); /* centers the 1px line on the edge */
}
/* 
.separator {
  position: absolute;
  top: 25%;
  bottom: 25%;
  width: 10px;
  background-color: var(--separator-color);
  background-color: white;
} */
</style>
